import React, { useState } from 'react';
import { Button } from './ui/button';
import { Calendar } from './ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { CalendarIcon, X } from 'lucide-react';
import { format } from 'date-fns';

const DateFilter = ({ 
  value = 'all', 
  onChange, 
  startDate = null, 
  endDate = null, 
  onDateRangeChange,
  showAllTime = true,
  className = ''
}) => {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);

  const handlePresetChange = (preset) => {
    if (preset === 'custom') {
      setTempStartDate(startDate);
      setTempEndDate(endDate);
      setShowDatePicker(true);
    } else {
      onChange(preset);
      if (onDateRangeChange) {
        onDateRangeChange(null, null);
      }
    }
  };

  const handleApplyDateRange = () => {
    if (tempStartDate && tempEndDate) {
      onChange('custom');
      if (onDateRangeChange) {
        onDateRangeChange(tempStartDate, tempEndDate);
      }
      setShowDatePicker(false);
    }
  };

  const handleClearDateRange = () => {
    setTempStartDate(null);
    setTempEndDate(null);
    onChange('all');
    if (onDateRangeChange) {
      onDateRangeChange(null, null);
    }
    setShowDatePicker(false);
  };

  const getDisplayText = () => {
    switch (value) {
      case 'today': return 'Today';
      case 'yesterday': return 'Yesterday';
      case 'weekly': return 'Last 7 Days';
      case 'monthly': return 'Last 30 Days';
      case 'custom':
        if (startDate && endDate) {
          return `${format(new Date(startDate), 'dd MMM')} - ${format(new Date(endDate), 'dd MMM yyyy')}`;
        }
        return 'Custom Range';
      default: return 'All Time';
    }
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Select value={value} onValueChange={handlePresetChange}>
        <SelectTrigger className="w-[160px]" data-testid="date-filter-select">
          <CalendarIcon className="w-4 h-4 mr-2 text-gray-500" />
          <SelectValue placeholder="Filter by date">{getDisplayText()}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          {showAllTime && <SelectItem value="all">All Time</SelectItem>}
          <SelectItem value="today">Today</SelectItem>
          <SelectItem value="yesterday">Yesterday</SelectItem>
          <SelectItem value="weekly">Last 7 Days</SelectItem>
          <SelectItem value="monthly">Last 30 Days</SelectItem>
          <SelectItem value="custom">Custom Range...</SelectItem>
        </SelectContent>
      </Select>

      {/* Custom Date Range Dialog */}
      <Dialog open={showDatePicker} onOpenChange={setShowDatePicker}>
        <DialogContent className="sm:max-w-fit" data-testid="date-range-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5" />
              Select Date Range
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex flex-col sm:flex-row gap-4 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Start Date</label>
              <Calendar
                mode="single"
                selected={tempStartDate}
                onSelect={setTempStartDate}
                disabled={(date) => date > new Date()}
                className="rounded-md border"
                initialFocus
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">End Date</label>
              <Calendar
                mode="single"
                selected={tempEndDate}
                onSelect={setTempEndDate}
                disabled={(date) => date > new Date() || (tempStartDate && date < tempStartDate)}
                className="rounded-md border"
              />
            </div>
          </div>
          
          {tempStartDate && tempEndDate && (
            <div className="text-sm text-center text-gray-600 py-2 bg-blue-50 rounded">
              Selected: {format(tempStartDate, 'dd MMM yyyy')} - {format(tempEndDate, 'dd MMM yyyy')}
            </div>
          )}
          
          <DialogFooter className="flex justify-between sm:justify-between">
            <Button variant="outline" onClick={handleClearDateRange}>
              <X className="w-4 h-4 mr-1" /> Clear
            </Button>
            <Button 
              onClick={handleApplyDateRange}
              disabled={!tempStartDate || !tempEndDate}
            >
              Apply Range
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Show selected range indicator with clear button */}
      {value === 'custom' && startDate && endDate && (
        <Button 
          variant="ghost" 
          size="sm" 
          className="h-8 px-2 text-gray-500 hover:text-red-500"
          onClick={handleClearDateRange}
          title="Clear date filter"
        >
          <X className="w-4 h-4" />
        </Button>
      )}
    </div>
  );
};

export default DateFilter;
